import React from 'react';
import { useParams, Navigate, Route, Routes } from 'react-router-dom';
import { Box } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import makeStyles from '@mui/styles/makeStyles';
import EventInfoStore from '../../../Stores/EventInfoStore';
import BasicInfo from '../BasicInfo/BasicInfo';
import RegistrationForm from '../RegistrationForm/RegistrationForm';
import Tickets from '../Tickets/Tickets';
import AddOns from '../AddOns/AddOns';
import FAQs from '../FAQs/FAQs';
import Coupons from '../Coupons/Coupons';
import ConfirmationEmail from '../ConfirmationEmail/ConfirmationEmail';
import EventNavSidebar from '../../AppNav/ManageEvents/EventNavSidebar';
import Loading from '../../Loading/Loading';
import { useEdition } from '../../../Hooks/useEdition';
import OrdersBase from '../../Orders/OrdersBase';
import Integration from '../../Integrations/Integration';
import WebhookBase from '../../Integrations/Webhook/WebhookBase';
import AffiliateBase from '../../Integrations/Affiliate/AffiliateBase';
import EventInfoUpdater from '../EventInfoUpdater/EventInfoUpdater';
import UnderReviewBanner from './UnderReviewBanner';
import CourseDetail from '../CourseDetail/CourseDetail';
import WhatsOffered from '../WhatsOffered/WhatsOffered';
import DashboardBase from '../../Dashboard/DashboardBase';
import EmailPreferences from '../../EmailPreferences/EmailPreferences';
import { useAuth } from '../../../Contexts/AuthContext';
import ProtectedRoute from '../../Common/ProtectedRoute/ProtectedRoute';
import { PageInfo, PagePermissions } from '../../../Consts/routePermissions';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  content: {
    flexGrow: 1,
  },
  stepContent: {
    padding: theme.spacing(4),
  },
  contentMargin: {
    margin: 'auto',
  },
}));

const ManageEventBase = () => {
  const classes = useStyles();
  const { eventId, editionId } = useParams();
  const { edition, isLoading, error } = useEdition(eventId, editionId);
  const { isAdmin } = useAuth();

  return (
    <div className={classes.root}>
      {!error && Boolean(edition) && (
        <EventInfoStore
          initialState={{
            ticketIndex: -1,
            event: { eventId, editionId },
            ...edition,
          }}
        >
          <EventNavSidebar />
          <div className={classes.content}>
            {edition?.underReview && (
              <UnderReviewBanner
                eventTitle={edition?.basicInfo?.eventTitle}
                eventId={eventId}
              />
            )}
            <Box className={classes.stepContent}>
              <Grid container spacing={3} className={classes.contentMargin}>
                <Grid xs={12}>
                  {isLoading && <Loading variant="spinner" />}
                  <Routes>
                    <Route
                      path={PageInfo.BasicInfo.route}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.BasicInfo?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <BasicInfo isManage={true} isUpdate={true} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={PageInfo.CourseDetails.route}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.CourseDetails?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <CourseDetail isManage={true} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={PageInfo.Tickets.route}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.Tickets?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <Tickets isManage={true} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={PageInfo.WhatsIncluded.route}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.WhatsIncluded?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <WhatsOffered isManage={true} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={PageInfo.AddOns.route}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.AddOns?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <AddOns />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={PageInfo.RegistrationForm.route}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.RegistrationForm?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <RegistrationForm />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={PageInfo.ConfirmationEmail.route}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.ConfirmationEmail?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <ConfirmationEmail isManage={true} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={PageInfo.Faqs.route}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.Faqs?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <FAQs />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${PageInfo.Orders.route}/*`}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.Orders?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <OrdersBase />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={PageInfo.Integration.route}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.Integration?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <Integration />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${PageInfo.Webhook.route}/*`}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.Webhook?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <WebhookBase />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${PageInfo.Affiliate.route}/*`}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.Affiliate?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <AffiliateBase />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={PageInfo.Coupons.route}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.Coupons?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <Coupons />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={PageInfo.EmailPreferences.route}
                      element={
                        <ProtectedRoute
                          isAllowed={
                            isAdmin ||
                            !(
                              PagePermissions?.EmailPreferences?.blockedPermissions?.includes(
                                edition.permissionLevel
                              ) || false
                            )
                          }
                          redirectPath={PageInfo.Dashboard.route}
                        >
                          <EmailPreferences />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={PageInfo.Dashboard.route}
                      element={<DashboardBase />}
                    />
                    <Route
                      path="/"
                      element={<Navigate to={PageInfo.BasicInfo.route} />}
                    />
                    <Route
                      path="*"
                      element={<Navigate to={PageInfo.BasicInfo.route} />}
                    />
                  </Routes>
                </Grid>
              </Grid>
            </Box>
          </div>
          <EventInfoUpdater edition={edition} />
        </EventInfoStore>
      )}
    </div>
  );
};

export default ManageEventBase;
